// Maps a flat rectangle onto an arbitrary 4-point quadrilateral using a 2D projective
// (homography) transform, expressed either as a CSS matrix3d() string or as a raw 3x3
// matrix for point projection. Standard "unit square to quad" construction (Heckbert).

function multmm(a, b) {
  const c = Array(9);
  for (let i = 0; i !== 3; ++i) {
    for (let j = 0; j !== 3; ++j) {
      let cij = 0;
      for (let k = 0; k !== 3; ++k) cij += a[3 * i + k] * b[3 * k + j];
      c[3 * i + j] = cij;
    }
  }
  return c;
}

function multmv(m, v) {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

function adj(m) {
  return [
    m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3],
  ];
}

function basisToPoints(x1, y1, x2, y2, x3, y3, x4, y4) {
  const m = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
  const v = multmv(adj(m), [x4, y4, 1]);
  return multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
}

// Returns the 3x3 (row-major, length-9) matrix mapping source quad -> dest quad.
// Point order for both quads must be: top-left, top-right, bottom-left, bottom-right.
export function general2DProjection(x1s, y1s, x2s, y2s, x3s, y3s, x4s, y4s, x1d, y1d, x2d, y2d, x3d, y3d, x4d, y4d) {
  const s = basisToPoints(x1s, y1s, x2s, y2s, x3s, y3s, x4s, y4s);
  const d = basisToPoints(x1d, y1d, x2d, y2d, x3d, y3d, x4d, y4d);
  return multmm(d, adj(s));
}

export function invert3x3(m) {
  const a = adj(m);
  const det = m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
  return a.map((v) => v / det);
}

// Projects point (u, v) through 3x3 matrix m, dividing out the homogeneous coordinate.
export function applyPoint(m, u, v) {
  const w = m[6] * u + m[7] * v + m[8];
  return [(m[0] * u + m[1] * v + m[2]) / w, (m[3] * u + m[4] * v + m[5]) / w];
}

// Builds a CSS matrix3d() that warps a flat w x h box (top-left at local 0,0) onto the
// given destination quad (absolute pixel points within the same positioned ancestor).
export function quadMatrix3d(w, h, dst) {
  const t = general2DProjection(0, 0, w, 0, 0, h, w, h, dst.tl[0], dst.tl[1], dst.tr[0], dst.tr[1], dst.bl[0], dst.bl[1], dst.br[0], dst.br[1]);
  for (let i = 0; i < 9; i++) t[i] = t[i] / t[8];
  const m = [t[0], t[3], 0, t[6], t[1], t[4], 0, t[7], 0, 0, 1, 0, t[2], t[5], 0, t[8]];
  return `matrix3d(${m.join(",")})`;
}
